import { useEffect, useRef, useState } from "react";
import "@/App.css";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Cursor from "@/components/site/Cursor";
import ScrollProgress from "@/components/site/ScrollProgress";
import Nav from "@/components/site/Nav";
import Hero from "@/components/site/Hero";
import Stats from "@/components/site/Stats";
import Manifesto from "@/components/site/Manifesto";
import Problem from "@/components/site/Problem";
import Partnerschaft from "@/components/site/Partnerschaft";
import UeberUns from "@/components/site/UeberUns";
import Ablauf from "@/components/site/Ablauf";
import Marquee from "@/components/site/Marquee";
import Kontakt from "@/components/site/Kontakt";
import Footer from "@/components/site/Footer";
import CookieConsent from "@/components/site/CookieConsent";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import { Toaster } from "@/components/ui/sonner";

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1.0,
      touchMultiplier: 1.2,
    });
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    window.__lenis = lenis;
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); window.__lenis = null; };
  }, []);
}

function LoadingIntro({ onDone }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now(); const duration = 500;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setProgress(p);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setTimeout(onDone, 120);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onDone]);
  return (
    <motion.div
      data-testid="intro-overlay"
      initial={{ y: 0 }}
      exit={{ y: "-100%" }}
      transition={{ duration: 0.55, ease: [0.83, 0, 0.17, 1] }}
      className="fixed inset-0 z-[9990] bg-[#0A0A0A] flex flex-col justify-end"
    >
      <div className="w-full px-6 md:px-10 pb-8">
        <div className="flex items-baseline justify-between mb-5">
          <span className="font-mono-eyebrow text-[#C9A961]">Hudak · Enterprises</span>
          <span className="font-mono-eyebrow text-[#FAFAF7]/70">{String(Math.round(progress * 100)).padStart(3, "0")}</span>
        </div>
        <div className="h-[1px] w-full bg-[#FAFAF7]/10 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-[#C9A961]" style={{ width: `${progress * 100}%` }} />
        </div>
      </div>
    </motion.div>
  );
}

function ScrollToHashOnLoad() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (pathname !== "/" || !hash) { window.scrollTo({ top: 0 }); return; }
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) setTimeout(() => {
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 300);
  }, [hash, pathname]);
  return null;
}

function HomePage() {
  const [introDone, setIntroDone] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("he_intro_seen") === "1";
  });
  const handleIntroDone = () => {
    try { sessionStorage.setItem("he_intro_seen", "1"); } catch {}
    setIntroDone(true);
  };
  return (
    <>
      <AnimatePresence>{!introDone && <LoadingIntro onDone={handleIntroDone} />}</AnimatePresence>
      <Nav ready={introDone} />
      <main>
        <Hero ready={introDone} />
        <Stats />
        <Manifesto />
        <Problem />
        <Partnerschaft />
        <UeberUns />
        <Ablauf />
        <Marquee />
        <Kontakt />
      </main>
      <Footer />
    </>
  );
}

function Shell() {
  useLenis();
  return (
    <div className="App relative">
      <Cursor />
      <ScrollProgress />
      <ScrollToHashOnLoad />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/impressum" element={<><Impressum /><Footer /></>} />
        <Route path="/datenschutz" element={<><Datenschutz /><Footer /></>} />
      </Routes>
      <CookieConsent />
      <Toaster position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

export default App;
